/**
 * Editor syntax-highlighting theme presets.
 *
 * Each theme provides light + dark variants that map semantic
 * `--syntax-*` CSS custom properties to concrete colors.
 * The active theme's variables are applied on `document.documentElement`
 * so the CodeMirror HighlightStyle (which references `var(--syntax-*)`)
 * picks them up automatically.
 */

export interface SyntaxColorSet {
  command: string;
  environment: string;
  math: string;
  string: string;
  comment: string;
  number: string;
  bracket: string;
}

export interface SyntaxTheme {
  id: string;
  name: string;
  light: SyntaxColorSet;
  dark: SyntaxColorSet;
}

export const SYNTAX_THEMES: SyntaxTheme[] = [
  {
    id: "lever",
    name: "Lever",
    light: {
      command: "#E05000",
      environment: "#C2410C",
      math: "#EA580C",
      string: "#16A34A",
      comment: "#71717A",
      number: "#2563EB",
      bracket: "#3F3F46",
    },
    dark: {
      command: "#FF5C00",
      environment: "#FB923C",
      math: "#FDBA74",
      string: "#4ADE80",
      comment: "#63636E",
      number: "#60A5FA",
      bracket: "#A1A1AA",
    },
  },
  {
    id: "monokai",
    name: "Monokai",
    light: {
      command: "#D6336C",
      environment: "#AE3EC9",
      math: "#E8590C",
      string: "#2B8A3E",
      comment: "#868E96",
      number: "#AE3EC9",
      bracket: "#495057",
    },
    dark: {
      command: "#F92672",
      environment: "#AE81FF",
      math: "#FD971F",
      string: "#A6E22E",
      comment: "#75715E",
      number: "#AE81FF",
      bracket: "#F8F8F2",
    },
  },
  {
    id: "solarized",
    name: "Solarized",
    light: {
      command: "#859900",
      environment: "#268BD2",
      math: "#CB4B16",
      string: "#2AA198",
      comment: "#93A1A1",
      number: "#D33682",
      bracket: "#586E75",
    },
    dark: {
      command: "#859900",
      environment: "#268BD2",
      math: "#CB4B16",
      string: "#2AA198",
      comment: "#586E75",
      number: "#D33682",
      bracket: "#93A1A1",
    },
  },
  {
    id: "nord",
    name: "Nord",
    light: {
      command: "#5E81AC",
      environment: "#81A1C1",
      math: "#D08770",
      string: "#A3BE8C",
      comment: "#9DA3AF",
      number: "#B48EAD",
      bracket: "#4C566A",
    },
    dark: {
      command: "#81A1C1",
      environment: "#88C0D0",
      math: "#D08770",
      string: "#A3BE8C",
      comment: "#616E88",
      number: "#B48EAD",
      bracket: "#D8DEE9",
    },
  },
  {
    id: "github",
    name: "GitHub",
    light: {
      command: "#CF222E",
      environment: "#8250DF",
      math: "#953800",
      string: "#0A3069",
      comment: "#6E7781",
      number: "#0550AE",
      bracket: "#24292F",
    },
    dark: {
      command: "#FF7B72",
      environment: "#D2A8FF",
      math: "#FFA657",
      string: "#A5D6FF",
      comment: "#8B949E",
      number: "#79C0FF",
      bracket: "#C9D1D9",
    },
  },
];

const SYNTAX_VARS: (keyof SyntaxColorSet)[] = [
  "command",
  "environment",
  "math",
  "string",
  "comment",
  "number",
  "bracket",
];

export function applySyntaxTheme(
  themeId: string,
  resolvedMode: "light" | "dark",
): void {
  const theme = SYNTAX_THEMES.find((t) => t.id === themeId) ?? SYNTAX_THEMES[0];
  const colors = theme[resolvedMode];
  const root = document.documentElement;
  for (const key of SYNTAX_VARS) {
    root.style.setProperty(`--syntax-${key}`, colors[key]);
  }
}

export function clearSyntaxTheme(): void {
  const root = document.documentElement;
  for (const key of SYNTAX_VARS) {
    root.style.removeProperty(`--syntax-${key}`);
  }
}
